import { useQyGetDashboardTransaction } from "@factory/query/transaction.query";
import "./dashboard.scss";
import { DashboardModel } from "@core/model/dashboard-model";
import {
  FaChildReaching,
  FaMoneyBill,
  FaMoneyBill1Wave,
} from "react-icons/fa6";
import DashboardCard from "@core/ui/dashboard-card/dashboard-card";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import { LabelValue } from "@shared/models/label-value.interface";
import { useState, useMemo } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import {
  FaChartLine,
  FaMoneyBillWave,
  FaMoneyCheckAlt,
  FaPuzzlePiece,
  FaUsers,
} from "react-icons/fa";
import { useQyGetPerson } from "@factory/query/person.query";
import { ServiceBreakdownItem } from "@factory/model/query-model-transaction";

/* eslint-disable-next-line */
export interface DashboardProps {}

export function Dashboard() {
  const [selectedDayFilter, setSelectedDayFilter] = useState("");
  const [selectedPersonFilter, setSelectedPersonFilter] = useState("");
  const [personOptions, setPersonOptions] = useState<LabelValue[]>([]);

  const dayFilterOption = [
    { label: "Today", value: "TODAY" },
    { label: "Yesterday", value: "YESTERDAY" },
    { label: "Week", value: "WEEK" },
    { label: "Month", value: "MONTH" },
  ] as LabelValue[];

  const {
    data: personResponse,
    isLoading: isPersonLoading,
    isError: isPersonError,
    error: personError,
  } = useQyGetPerson("", 1000, 0, {}, {});

  useMemo(() => {
    if (personResponse?.data) {
      const personLabelValues = personResponse.data.map((person: any) => {
        const firstName = person.first_name || "";
        const middleName = person.middle_name ? ` ${person.middle_name}` : "";
        const lastName = person.last_name || "";
        const completeName = `${firstName}${middleName} ${lastName}`.trim();

        return new LabelValue(completeName || "Unknown Person", person.code);
      });

      personLabelValues.sort((a, b) => a.label.localeCompare(b.label));
      setPersonOptions(personLabelValues);
    }
  }, [personResponse]);

  const dashboardFilter = useMemo(() => {
    const filters: Record<string, string> = {};

    if (selectedPersonFilter) {
      filters.created_by = selectedPersonFilter;
    }

    return filters;
  }, [selectedPersonFilter]);

  const { data, isLoading, error, isError } = useQyGetDashboardTransaction(
    "",
    dashboardFilter,
    selectedDayFilter,
  );

  const summaryData = data?.summary || [];
  const serviceSummaryData = data?.service_summary;
  const serviceBreakdownData = data?.service_breakdown || [];

  const serviceSummaryCards = useMemo(() => {
    const totalKids = serviceSummaryData?.total_kids || 0;
    const serviceRevenue = serviceSummaryData?.service_revenue || 0;
    const addonRevenue = serviceSummaryData?.addon_revenue || 0;
    const grandTotal = serviceSummaryData?.grand_total || 0;

    return {
      totalKidsCard: new DashboardModel(
        "Total Kids",
        totalKids,
        <FaUsers />,
        undefined,
        undefined,
        "bg-blue-200",
      ),
      serviceRevenueCard: new DashboardModel(
        "Service Revenue",
        currencyFormat(serviceRevenue, "PHP"),
        <FaMoneyBillWave />,
        undefined,
        undefined,
        "bg-green-200",
      ),
      addonRevenueCard: new DashboardModel(
        "Addon Revenue",
        currencyFormat(addonRevenue, "PHP"),
        <FaPuzzlePiece />,
        undefined,
        undefined,
        "bg-purple-200",
      ),
      grandTotalCard: new DashboardModel(
        "Grand Total",
        currencyFormat(grandTotal, "PHP"),
        <FaChartLine />,
        undefined,
        undefined,
        "bg-orange-200",
      ),
    };
  }, [serviceSummaryData]);

  const dayFilterElement = (
    <Dropdown
      value={selectedDayFilter}
      onChange={(e) => setSelectedDayFilter(e.value)}
      options={dayFilterOption}
      placeholder="Select a day filter"
      className="w-full"
    />
  );

  const personFilterElement = (
    <Dropdown
      value={selectedPersonFilter}
      onChange={(e) => {
        setSelectedPersonFilter(e.value);
      }}
      options={personOptions}
      placeholder={isPersonLoading ? "Loading persons..." : "Filter by person"}
      className="w-full"
      showClear
      disabled={isPersonLoading || personOptions.length === 0}
      filter
      filterPlaceholder="Search persons..."
    />
  );

  const children = new DashboardModel(
    summaryData?.[0]?.label || "-",
    summaryData?.[0]?.value || 0,
    <FaChildReaching />,
    undefined,
    undefined,
    "bg-green-200",
  );
  const totalpay = new DashboardModel(
    summaryData?.[1]?.label || "-",
    summaryData?.[1]?.value || 0,
    <FaMoneyBill1Wave />,
    undefined,
    undefined,
    "bg-amber-200",
  );
  const totalunpaid = new DashboardModel(
    summaryData?.[2]?.label || "-",
    summaryData?.[2]?.value || 0,
    <FaMoneyCheckAlt />,
    undefined,
    undefined,
    "bg-blue-200",
  );

  const totalamountpaid = new DashboardModel(
    summaryData?.[3]?.label || "-",
    summaryData?.[3]?.value || 0,
    <FaMoneyBill />,
    undefined,
    undefined,
    "bg-green-200",
  );

  const serviceNameTemplate = (rowData: ServiceBreakdownItem) => {
    return <span className="font-medium">{rowData.service_name}</span>;
  };

  const rateTemplate = (rowData: ServiceBreakdownItem) => {
    return <span>{currencyFormat(rowData.rate, "PHP")}</span>;
  };

  const quantityTemplate = (rowData: ServiceBreakdownItem) => {
    return (
      <div className="text-center">
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
          {rowData.quantity} kids
        </span>
      </div>
    );
  };

  const totalTemplate = (rowData: ServiceBreakdownItem) => {
    return (
      <span className="font-bold text-green-600">
        {currencyFormat(rowData.total, "PHP")}
      </span>
    );
  };

  const addonTemplate = (rowData: ServiceBreakdownItem) => {
    if (!rowData.addons || rowData.addons.length === 0) {
      return <span className="text-gray-400 italic">No addons</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        {rowData.addons.map((addon, index) => (
          <div key={index} className="bg-gray-50 p-2 rounded border text-sm">
            <div className="font-medium">{addon.item_name}</div>
            <div className="text-gray-600">
              Qty: {addon.quantity} × {currencyFormat(addon.amount, "PHP")} ={" "}
              <span className="font-medium text-green-600">
                {currencyFormat(addon.total, "PHP")}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );

  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occurred" message={(error as any)?.message} />
    </div>
  );

  const isLoadingAny = isLoading;
  const isErrorAny = isError;

  const grid = (
    <div>
      <h1 className="mb-4">Dashboard</h1>
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <DashboardCard model={children} />
        <DashboardCard model={totalpay} />
        <DashboardCard model={totalunpaid} />
        <DashboardCard model={totalamountpaid} />
      </section>

      <div className="mt-6 mb-4">
        <h3 className="text-lg font-bold text-gray-800 mb-4">
          Service Summary
        </h3>
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <DashboardCard model={serviceSummaryCards.serviceRevenueCard} />
          <DashboardCard model={serviceSummaryCards.addonRevenueCard} />
          <DashboardCard model={serviceSummaryCards.grandTotalCard} />
        </section>
      </div>

      <section className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            Services for{" "}
            {selectedDayFilter
              ? dayFilterOption.find((opt) => opt.value === selectedDayFilter)
                  ?.label
              : "All Time"}
          </h2>
          <div className="text-sm text-gray-600">
            Total Services:{" "}
            <span className="font-bold">{serviceBreakdownData.length}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          <DataTable
            value={serviceBreakdownData}
            className="p-datatable-sm"
            stripedRows
            showGridlines
            responsiveLayout="scroll"
            emptyMessage="No services found for the selected period"
          >
            <Column
              field="service_name"
              header="Service Name"
              body={serviceNameTemplate}
              style={{ minWidth: "200px" }}
              sortable
            />
            <Column
              field="rate"
              header="Rate"
              body={rateTemplate}
              style={{ minWidth: "120px" }}
              sortable
            />
            <Column
              field="quantity"
              header="Quantity"
              body={quantityTemplate}
              style={{ minWidth: "140px" }}
              sortable
            />
            <Column
              field="total"
              header="Total"
              body={totalTemplate}
              style={{ minWidth: "130px" }}
              sortable
            />
            <Column
              field="addons"
              header="Addon Items"
              body={addonTemplate}
              style={{ minWidth: "300px" }}
            />
          </DataTable>
        </div>
      </section>
    </div>
  );

  // Local Function
  const handleClear = () => {
    setSelectedDayFilter("");
    setSelectedPersonFilter("");
  };

  return (
    <div id="Dashboard" className="dashboard p-7">
      {/* <section>
        <h4 className="text-gray-500">Filters</h4>
        <section className="flex justify-between gap-4 mb-6">
          {dayFilterElement}
          <Button
            className="block w-28"
            severity="secondary"
            outlined
            label="Clear"
            onClick={handleClear}
          />
        </section>
      </section> */}
      <section>
        <h4 className="text-gray-500">Filters</h4>
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            {dayFilterElement}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Created By Person
            </label>
            {personFilterElement}
          </div>

          <div className="flex items-end">
            <Button
              className="w-full"
              severity="secondary"
              outlined
              label="Clear All Filters"
              onClick={handleClear}
              disabled={!selectedDayFilter && !selectedPersonFilter}
            />
          </div>
        </section>

        {(selectedDayFilter || selectedPersonFilter) && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h5 className="text-sm font-medium text-blue-800 mb-2">
              Active Filters:
            </h5>
            <div className="flex flex-wrap gap-2">
              {selectedDayFilter && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Date:{" "}
                  {
                    dayFilterOption.find(
                      (opt) => opt.value === selectedDayFilter,
                    )?.label
                  }
                  <button
                    onClick={() => setSelectedDayFilter("")}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedPersonFilter && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Person:{" "}
                  {
                    personOptions.find((p) => p.value === selectedPersonFilter)
                      ?.label
                  }
                  <button
                    onClick={() => setSelectedPersonFilter("")}
                    className="ml-1 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </section>

      {isLoadingAny && displayLoading}
      {isErrorAny && !isLoadingAny && displayError}
      {!isLoadingAny && !isErrorAny && grid}
    </div>
  );
}

export default Dashboard;
